
import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { AlertCircle, Camera, CheckCircle, XCircle, Loader2, Lock } from 'lucide-react';

interface QRScannerProps {
  onScan: (data: string) => void;
  isProcessing: boolean;
}

const QRScanner: React.FC<QRScannerProps> = ({ onScan, isProcessing }) => {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isLoadingCamera, setIsLoadingCamera] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerId = "reader";

  useEffect(() => {
    // HTTPS Check
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1')) {
        setError("Kamera erişimi için güvenli bağlantı (HTTPS) gereklidir.");
    }

    // Cleanup function when component unmounts
    return () => {
      if (scannerRef.current) {
        if (scannerRef.current.isScanning) {
           scannerRef.current.stop().then(() => {
               scannerRef.current?.clear();
           }).catch(err => console.warn("Scanner cleanup error", err));
        } else {
            scannerRef.current.clear();
        }
      }
    };
  }, []);

  const startScan = async () => {
    setError(null);
    setIsLoadingCamera(true);
    
    try {
      // Ensure previous instance is stopped
      if (scannerRef.current) {
          try {
            if (scannerRef.current.isScanning) {
                await scannerRef.current.stop();
            }
            scannerRef.current.clear();
          } catch(e) { /* ignore cleanup errors */ }
      }

      const html5QrCode = new Html5Qrcode(scannerId);
      scannerRef.current = html5QrCode;
      
      // OPTIMIZED CONFIGURATION FOR MOBILE
      const config = {
          fps: 10, 
          // Use a function to determine scan box size relative to viewport
          qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
              const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
              return {
                  width: Math.floor(minEdge * 0.7),
                  height: Math.floor(minEdge * 0.7)
              };
          },
          // Important: Removed aspectRatio to allow full camera usage (fixes stretch issues)
          disableFlip: false
      };

      await html5QrCode.start(
        { facingMode: "environment" }, // Request back camera
        config,
        (decodedText) => { 
            // Pause scanning on success to prevent multiple triggers
            if (!isProcessing) { 
                html5QrCode.pause(); 
                onScan(decodedText); 
            } 
        },
        (errorMessage) => { 
            // Ignore scan errors, they happen on every frame without a QR
        }
      );
      
      setIsScanning(true);
    } catch (err: any) {
      console.error("Camera start error:", err);
      setIsScanning(false);
      
      if (err.name === 'NotReadableError' || err.name === 'NotAllowedError') {
          setError("Kameraya erişilemedi. Lütfen tarayıcı ayarlarından kamera izni verin.");
      } else if (err.name === 'NotFoundError') {
          setError("Cihazda kamera bulunamadı.");
      } else {
          setError("Kamera başlatılamadı: " + (err.message || "Bilinmeyen hata"));
      }
    } finally {
        setIsLoadingCamera(false);
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { 
          if(scannerRef.current.isScanning) {
            await scannerRef.current.stop(); 
          }
          scannerRef.current.clear(); 
      } 
      catch (err) { console.warn("Stop error", err); } 
      finally { setIsScanning(false); }
    }
  };

  return (
    <div className="flex flex-col items-center w-full max-w-md mx-auto px-4">
      {/* Scanner Box */}
      <div className="relative w-full aspect-square max-w-[350px] overflow-hidden rounded-3xl bg-black border-2 border-slate-700 shadow-2xl mb-6">
          <div id={scannerId} className="w-full h-full" />
          
          {/* Loading Overlay */}
          {isLoadingCamera && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 z-10">
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                  <span className="text-white font-bold text-sm">Kamera Başlatılıyor...</span>
              </div>
          )}

          {/* Idle State Overlay (When not scanning) */}
          {!isScanning && !isLoadingCamera && !isProcessing && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10 p-6 text-center">
                  <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4 shadow-lg">
                      <Camera className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-300 text-sm font-medium">QR kodu okutmak için kamerayı başlatın.</p>
              </div>
          )}
          
          {/* Scan Guide Overlay (Only visible when scanning) */}
          {isScanning && !isLoadingCamera && (
              <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center">
                  <div className="w-[70%] h-[70%] border-2 border-blue-500/50 rounded-2xl relative">
                      <div className="absolute top-0 left-0 w-4 h-4 border-l-4 border-t-4 border-blue-500 -ml-1 -mt-1 rounded-tl-lg"></div>
                      <div className="absolute top-0 right-0 w-4 h-4 border-r-4 border-t-4 border-blue-500 -mr-1 -mt-1 rounded-tr-lg"></div>
                      <div className="absolute bottom-0 left-0 w-4 h-4 border-l-4 border-b-4 border-blue-500 -ml-1 -mb-1 rounded-bl-lg"></div>
                      <div className="absolute bottom-0 right-0 w-4 h-4 border-r-4 border-b-4 border-blue-500 -mr-1 -mb-1 rounded-br-lg"></div>
                  </div>
              </div>
          )}
      </div>

      {/* Controls */}
      {!isScanning && !isProcessing && !isLoadingCamera && !error && (
        <button onClick={startScan} className="flex items-center justify-center w-full py-4 px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl shadow-lg shadow-blue-900/30 transition-all active:scale-95 text-lg font-bold gap-3 group">
          <div className="p-2 bg-blue-700 rounded-xl group-hover:bg-blue-600 transition-colors"><Camera className="w-6 h-6" /></div>
          Kamerayı Başlat
        </button>
      )}

      {(isScanning || isLoadingCamera) && (
        <button onClick={stopScan} disabled={isLoadingCamera} className="w-full py-4 px-6 bg-red-600/90 hover:bg-red-500 text-white rounded-2xl font-bold shadow-lg shadow-red-900/30 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
          <XCircle className="w-5 h-5"/> {isLoadingCamera ? 'Bekleyiniz...' : 'İptal Et'}
        </button>
      )}

      {/* Processing State */}
      {isProcessing && (
        <div className="flex flex-col items-center text-green-400 font-bold animate-pulse gap-2 mt-4">
          <CheckCircle className="w-12 h-12" />
          <span className="text-xl">İşleniyor...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 text-red-200 rounded-2xl flex items-start text-sm text-left w-full shadow-lg">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-red-500" />
          <div className="flex flex-col gap-1">
              <span className="font-bold">Hata:</span>
              <span>{error}</span>
              {error.includes("HTTPS") && <span className="text-xs text-slate-400 mt-1">Güvenlik gereği tarayıcılar kamerayı sadece HTTPS üzerinden açar.</span>}
          </div>
        </div>
      )}
      
      {isScanning && !isLoadingCamera && <div className="text-xs text-slate-400 mt-6 text-center font-medium bg-slate-900/50 px-4 py-2 rounded-full border border-slate-800">QR kodunu çerçeveye hizalayın.</div>}
    </div>
  );
};

export default QRScanner;
